/** Scaled solar system for the game.
 *
 *  We use real-ish physical properties (mass, radius, atmosphere height, orbit radius)
 *  but scaled down so flight feels reasonable at game speeds. Units: SI.
 *
 *  GM (mu) is the standard gravitational parameter. Orbits are treated as circular
 *  around the parent (good enough for patched-conics gameplay). */

export interface CelestialBody {
  id: string;
  name: string;
  color: string;
  /** Equatorial radius, m. */
  radius: number;
  /** Surface gravitational parameter GM, m^3/s^2 */
  mu: number;
  /** Sphere of influence radius, m. Beyond this, you belong to the parent. */
  soi: number;
  /** Atmosphere top (m) — 0 for no atmosphere. */
  atmosphereHeight: number;
  /** Sea-level air density kg/m³. */
  airDensitySL: number;
  /** Scale height m for exponential atmosphere. */
  scaleHeight: number;
  /** Parent body id, or undefined for the Sun. */
  parent?: string;
  /** Orbital radius around parent, m. */
  orbitRadius?: number;
  /** Orbital period around parent, s. */
  orbitPeriod?: number;
}

export const BODIES: CelestialBody[] = [
  {
    id: "sun",
    name: "Sun",
    color: "#ffd36b",
    radius: 6.957e8,
    mu: 1.327e20,
    soi: Number.POSITIVE_INFINITY,
    atmosphereHeight: 0,
    airDensitySL: 0,
    scaleHeight: 1,
  },
  {
    id: "mercury",
    name: "Mercury",
    color: "#9a8a7a",
    radius: 2.44e6,
    mu: 2.2e13,
    soi: 1.12e8,
    atmosphereHeight: 0,
    airDensitySL: 0,
    scaleHeight: 1,
    parent: "sun",
    orbitRadius: 5.79e10,
    orbitPeriod: 87.97 * 86400,
  },
  {
    id: "venus",
    name: "Venus",
    color: "#d9b07a",
    radius: 6.05e6,
    mu: 3.25e14,
    soi: 6.16e8,
    atmosphereHeight: 80_000,
    airDensitySL: 65,
    scaleHeight: 15_900,
    parent: "sun",
    orbitRadius: 1.08e11,
    orbitPeriod: 224.7 * 86400,
  },
  {
    id: "earth",
    name: "Earth",
    color: "#4aa8ff",
    radius: 6.371e6,
    mu: 3.986e14,
    soi: 9.29e8,
    atmosphereHeight: 70_000,
    airDensitySL: 1.225,
    scaleHeight: 7500,
    parent: "sun",
    orbitRadius: 1.496e11,
    orbitPeriod: 365.25 * 86400,
  },
  {
    id: "moon",
    name: "Moon",
    color: "#c7cbd1",
    radius: 1.737e6,
    mu: 4.9e12,
    soi: 6.6e7,
    atmosphereHeight: 0,
    airDensitySL: 0,
    scaleHeight: 1,
    parent: "earth",
    orbitRadius: 3.844e8,
    orbitPeriod: 27.32 * 86400,
  },
  {
    id: "mars",
    name: "Mars",
    color: "#c86a4a",
    radius: 3.39e6,
    mu: 4.28e13,
    soi: 5.77e8,
    atmosphereHeight: 40_000,
    airDensitySL: 0.02,
    scaleHeight: 11_100,
    parent: "sun",
    orbitRadius: 2.28e11,
    orbitPeriod: 686.97 * 86400,
  },
];

export const BODY_BY_ID = new Map(BODIES.map((b) => [b.id, b]));

export function bodyById(id: string): CelestialBody {
  const b = BODY_BY_ID.get(id);
  if (!b) throw new Error(`Unknown body: ${id}`);
  return b;
}

/** Current position of `body` in its parent's frame (2D, x-right, y-up). Deterministic from t. */
export function bodyPosition(body: CelestialBody, t: number): { x: number; y: number } {
  if (!body.parent || !body.orbitRadius || !body.orbitPeriod) return { x: 0, y: 0 };
  const theta = (2 * Math.PI * t) / body.orbitPeriod;
  return {
    x: body.orbitRadius * Math.cos(theta),
    y: body.orbitRadius * Math.sin(theta),
  };
}

/** Air density at a given altitude above body surface. */
export function airDensity(body: CelestialBody, altitude: number) {
  if (body.airDensitySL <= 0 || altitude >= body.atmosphereHeight) return 0;
  return body.airDensitySL * Math.exp(-altitude / body.scaleHeight);
}

/** Gravity acceleration at (position relative to body center). */
export function gravity(body: CelestialBody, rx: number, ry: number) {
  const r2 = rx * rx + ry * ry;
  const r = Math.sqrt(r2);
  const a = -body.mu / r2;
  return { ax: (a * rx) / r, ay: (a * ry) / r };
}

/** Return absolute position of a body in the Sun's (global) frame at time t. */
export function absolutePosition(bodyId: string, t: number): { x: number; y: number } {
  const chain: CelestialBody[] = [];
  let cur: CelestialBody | undefined = bodyById(bodyId);
  while (cur) {
    chain.push(cur);
    cur = cur.parent ? bodyById(cur.parent) : undefined;
  }
  let x = 0, y = 0;
  for (const b of chain) {
    const p = bodyPosition(b, t);
    x += p.x;
    y += p.y;
  }
  return { x, y };
}

/** Given a vessel's body-relative position (rx, ry in the `currentParentId` frame) and sim time,
 *  decide which body's SOI the vessel should belong to. Prefers the smallest/deepest SOI that
 *  actually contains the vessel. Handles child-of-current (enter) and parent-of-current (exit). */
export function resolveParent(
  currentParentId: string,
  rx: number,
  ry: number,
  t: number,
): string {
  const currentBody = bodyById(currentParentId);

  // 1. Check child bodies of the current parent — if vessel is inside their SOI, switch to child.
  const children = BODIES.filter((b) => b.parent === currentParentId);
  let bestChild: { id: string; d: number } | undefined;
  for (const c of children) {
    const pos = bodyPosition(c, t);
    const dx = rx - pos.x;
    const dy = ry - pos.y;
    const d = Math.hypot(dx, dy);
    if (d < c.soi) {
      if (!bestChild || d < bestChild.d) bestChild = { id: c.id, d };
    }
  }
  if (bestChild) return bestChild.id;

  // 2. If vessel is beyond current body's SOI, fall up to its parent.
  const r = Math.hypot(rx, ry);
  if (currentBody.parent && r > currentBody.soi) {
    return currentBody.parent;
  }

  return currentParentId;
}
